/**
 * Máquina de status da reserva — espelho de `StatusReserva.java` (18 estados).
 *
 * As transições aqui existem para a UI **não oferecer** um movimento que o backend
 * vai recusar. O backend continua sendo a autoridade: ele revalida toda transição.
 */
export type StatusReserva =
  // Cadastro (consultor)
  | 'RASCUNHO'
  | 'ENVIADA'
  // Análise Originei
  | 'EM_ANALISE_ORIGINEI'
  | 'COM_PENDENCIA'
  | 'APROVADA_ORIGINEI'
  | 'REPROVADA_ORIGINEI'
  // Estruturação / Parceiro
  | 'EM_ESTRUTURACAO'
  | 'EM_NEGOCIACAO'
  | 'EM_ANALISE_PARCEIRO'
  | 'APROVADA_PARCEIRO'
  | 'REPROVADA_PARCEIRO'
  // Contratação / Operação
  | 'EM_CONTRATACAO'
  | 'CONTRATO_ASSINADO'
  | 'EM_OPERACIONALIZACAO'
  | 'ATIVO'
  // Encerramento (terminais)
  | 'ENCERRADA'
  | 'CANCELADA'
  | 'NAO_CONVERTIDA'

/** Corpo do `PATCH /v1/oportunidades/{id}/status` — espelho de `dto/AtualizarStatusRequest`. */
export interface AtualizarStatusRequest {
  novoStatus: StatusReserva
  observacao?: string
  /** Ignorado pelo backend desde 28/09 (o autor vem do token); mantido por compatibilidade. */
  usuarioId: string
  /**
   * Código do catálogo `motivos_perda` (decisão 8.6). Obrigatório quando o destino é
   * CANCELADA ou NAO_CONVERTIDA — 400 `MOTIVO_PERDA_OBRIGATORIO` /
   * `MOTIVO_PERDA_OBSERVACAO_OBRIGATORIA`; ignorado nas demais transições.
   */
  motivoPerdaCodigo?: string
  /**
   * Controle otimista (PDF §2.4): a `versao` que a tela leu. Divergente → 409
   * `CONFLITO_VERSAO`, sem tocar na reserva. Ausente = sem checagem explícita.
   */
  versaoEsperada?: number | null
}

/** Rótulo legível de cada status. */
export const STATUS_LABEL: Record<StatusReserva, string> = {
  RASCUNHO: 'Rascunho',
  // Revisão de 06/09: a reserva enviada fica "aguardando aprovação" do time Originei.
  ENVIADA: 'Aguardando aprovação',
  EM_ANALISE_ORIGINEI: 'Em análise Originei',
  COM_PENDENCIA: 'Com pendência',
  APROVADA_ORIGINEI: 'Aprovada Originei',
  REPROVADA_ORIGINEI: 'Reprovada Originei',
  EM_ESTRUTURACAO: 'Em estruturação',
  EM_NEGOCIACAO: 'Em negociação',
  EM_ANALISE_PARCEIRO: 'Em análise parceiro',
  APROVADA_PARCEIRO: 'Aprovada parceiro',
  REPROVADA_PARCEIRO: 'Reprovada parceiro',
  EM_CONTRATACAO: 'Em contratação',
  CONTRATO_ASSINADO: 'Contrato assinado',
  EM_OPERACIONALIZACAO: 'Em operacionalização',
  ATIVO: 'Ativo',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
  NAO_CONVERTIDA: 'Não convertida',
}

/**
 * Reunião só se agenda depois que a Originei APROVOU a reserva (revisão de 06/09) —
 * espelho de `StatusReserva.permiteAgendamento()` do backend, que é quem recusa
 * (400 `RESERVA_NAO_APROVADA`). Vale de APROVADA_ORIGINEI em diante enquanto a reserva
 * estiver viva; COM_PENDENCIA fica de fora porque também ocorre antes da aprovação.
 */
export const STATUS_COM_AGENDAMENTO: ReadonlySet<StatusReserva> = new Set<StatusReserva>([
  'APROVADA_ORIGINEI', 'EM_ESTRUTURACAO', 'EM_NEGOCIACAO', 'EM_ANALISE_PARCEIRO',
  'APROVADA_PARCEIRO', 'REPROVADA_PARCEIRO', 'EM_CONTRATACAO', 'CONTRATO_ASSINADO',
  'EM_OPERACIONALIZACAO', 'ATIVO',
])

/** Status desconhecido/nulo (backend antigo) = fail-closed: não oferece o agendamento. */
export const permiteAgendamento = (status: string | null | undefined): boolean =>
  !!status && STATUS_COM_AGENDAMENTO.has(status as StatusReserva)

export const MOTIVO_AGENDAMENTO_INDISPONIVEL =
  'Disponível após a aprovação da reserva pela Originei.'

/** Fase da jornada — agrupa as colunas do Kanban. */
export const STATUS_FASE: Record<StatusReserva, string> = {
  RASCUNHO: 'Cadastro',
  ENVIADA: 'Cadastro',
  EM_ANALISE_ORIGINEI: 'Análise Originei',
  COM_PENDENCIA: 'Análise Originei',
  APROVADA_ORIGINEI: 'Análise Originei',
  REPROVADA_ORIGINEI: 'Análise Originei',
  EM_ESTRUTURACAO: 'Estruturação / Parceiro',
  EM_NEGOCIACAO: 'Estruturação / Parceiro',
  EM_ANALISE_PARCEIRO: 'Estruturação / Parceiro',
  APROVADA_PARCEIRO: 'Estruturação / Parceiro',
  REPROVADA_PARCEIRO: 'Estruturação / Parceiro',
  EM_CONTRATACAO: 'Contratação / Operação',
  CONTRATO_ASSINADO: 'Contratação / Operação',
  EM_OPERACIONALIZACAO: 'Contratação / Operação',
  ATIVO: 'Contratação / Operação',
  ENCERRADA: 'Encerramento',
  CANCELADA: 'Encerramento',
  NAO_CONVERTIDA: 'Encerramento',
}

const COR_FASE: Record<string, string> = {
  'Cadastro': '#8395a7',
  'Análise Originei': '#F39C12',
  'Estruturação / Parceiro': '#8E44AD',
  'Contratação / Operação': '#2ECC71',
  'Encerramento': '#95a5a6',
}

export const corDoStatus = (s: StatusReserva): string => COR_FASE[STATUS_FASE[s]] ?? '#8395a7'

/**
 * Fases na ordem da jornada, derivadas do próprio STATUS_FASE — não é uma segunda lista
 * para manter em dia. O Dashboard agrupa por aqui para não inventar uma terceira
 * taxonomia: até 14/08/2026 ele contava pelos 4 estágios legados enquanto o Kanban usava
 * os 18 status, e os dois números não se reconciliavam.
 */
export const FASES_JORNADA: string[] = [...new Set(Object.values(STATUS_FASE))]

export const corDaFase = (fase: string): string => COR_FASE[fase] ?? '#8395a7'

/**
 * Soma o valor estimado por status dentro de cada fase (Fase 6, item 24). Mesmo
 * agrupamento de `totalDaFase` — contagem e volume nunca podem discordar sobre o que
 * pertence a cada fase.
 */
export const valorDaFase = (
  valorPorStatus: Record<string, number> | undefined,
  fase: string,
): number =>
  (Object.keys(STATUS_FASE) as StatusReserva[])
    .filter((s) => STATUS_FASE[s] === fase)
    .reduce((acc, s) => acc + Number(valorPorStatus?.[s] ?? 0), 0)

/**
 * Leitura comercial em seis etapas (decisão 6.4, Handoff v4.0) — a camada do consultor e do
 * Dashboard. É DERIVADA no backend (`EtapaComercial.de`) a partir do status + primeiro
 * contato: "Reunião" não é status, e por isso o frontend não consegue agrupar só pelos 18.
 * O back-office continua nos 18 status (Kanban). Ordem = a do funil.
 */
export type EtapaComercial = 'RESERVA' | 'REUNIAO' | 'ESTRUTURACAO' | 'NEGOCIACAO' | 'GANHO' | 'PERDIDO'

export const ETAPAS_COMERCIAIS: EtapaComercial[] = [
  'RESERVA', 'REUNIAO', 'ESTRUTURACAO', 'NEGOCIACAO', 'GANHO', 'PERDIDO',
]

export const ETAPA_COMERCIAL_LABEL: Record<EtapaComercial, string> = {
  RESERVA: 'Reserva',
  REUNIAO: 'Reunião',
  ESTRUTURACAO: 'Estruturação',
  NEGOCIACAO: 'Negociação',
  GANHO: 'Ganho',
  PERDIDO: 'Perdido',
}

export const ETAPA_COMERCIAL_COR: Record<EtapaComercial, string> = {
  RESERVA: '#8395a7',
  REUNIAO: '#3578e5',
  ESTRUTURACAO: '#8E44AD',
  NEGOCIACAO: '#F39C12',
  GANHO: '#2ECC71',
  PERDIDO: '#95a5a6',
}

/** Cor MUI do chip da etapa comercial (lista/detalhe). */
export const etapaComercialChipColor = (
  e: EtapaComercial,
): 'default' | 'info' | 'secondary' | 'warning' | 'success' => {
  switch (e) {
    case 'REUNIAO': return 'info'
    case 'ESTRUTURACAO': return 'secondary'
    case 'NEGOCIACAO': return 'warning'
    case 'GANHO': return 'success'
    default: return 'default'
  }
}

/**
 * Canais comerciais (decisão 6.7, 19/08) — lista fechada, códigos do documento. A reserva
 * carimba o canal vigente do consultor na criação; comparativos são do admin.
 */
export type CanalComercial =
  | 'DIRECT' | 'CONSULTANT' | 'STRUCTURED_CONSULTANT_CHANNEL' | 'STRUCTURED_CONSULTANT_WHITE_LABEL'

export const CANAL_LABEL: Record<CanalComercial, string> = {
  DIRECT: 'Canal Direto',
  CONSULTANT: 'Consultor',
  STRUCTURED_CONSULTANT_CHANNEL: 'Canal Consultor Estruturado',
  STRUCTURED_CONSULTANT_WHITE_LABEL: 'Consultor Estruturado (White Label)',
}

export const CANAIS_COMERCIAIS: CanalComercial[] = [
  'DIRECT', 'CONSULTANT', 'STRUCTURED_CONSULTANT_CHANNEL', 'STRUCTURED_CONSULTANT_WHITE_LABEL',
]

/** Soma as contagens por status (como o backend devolve) dentro de cada fase. */
export const totalDaFase = (porStatus: Record<string, number>, fase: string): number =>
  (Object.keys(STATUS_FASE) as StatusReserva[])
    .filter((s) => STATUS_FASE[s] === fase)
    .reduce((acc, s) => acc + (porStatus[s] ?? 0), 0)

/**
 * Transições permitidas — cópia fiel do mapa em `StatusReserva.java`.
 * Se as duas divergirem, o backend recusa e a UI mostra o erro; nunca o contrário.
 */
export const TRANSICOES: Record<StatusReserva, StatusReserva[]> = {
  RASCUNHO: ['ENVIADA', 'CANCELADA'],
  ENVIADA: ['EM_ANALISE_ORIGINEI', 'CANCELADA'],
  EM_ANALISE_ORIGINEI: ['COM_PENDENCIA', 'APROVADA_ORIGINEI', 'REPROVADA_ORIGINEI', 'CANCELADA'],
  COM_PENDENCIA: ['EM_ANALISE_ORIGINEI', 'EM_ANALISE_PARCEIRO', 'CANCELADA'],
  APROVADA_ORIGINEI: ['EM_ESTRUTURACAO', 'CANCELADA'],
  REPROVADA_ORIGINEI: ['ENCERRADA'],
  EM_ESTRUTURACAO: ['EM_NEGOCIACAO', 'EM_ANALISE_PARCEIRO', 'CANCELADA'],
  EM_NEGOCIACAO: ['EM_ANALISE_PARCEIRO', 'CANCELADA'],
  EM_ANALISE_PARCEIRO: ['COM_PENDENCIA', 'APROVADA_PARCEIRO', 'REPROVADA_PARCEIRO', 'CANCELADA'],
  APROVADA_PARCEIRO: ['EM_CONTRATACAO', 'CANCELADA'],
  REPROVADA_PARCEIRO: ['EM_ESTRUTURACAO', 'NAO_CONVERTIDA', 'ENCERRADA'],
  EM_CONTRATACAO: ['CONTRATO_ASSINADO', 'NAO_CONVERTIDA', 'CANCELADA'],
  CONTRATO_ASSINADO: ['EM_OPERACIONALIZACAO', 'NAO_CONVERTIDA'],
  EM_OPERACIONALIZACAO: ['ATIVO', 'NAO_CONVERTIDA'],
  ATIVO: ['ENCERRADA'],
  ENCERRADA: [],
  CANCELADA: [],
  NAO_CONVERTIDA: [],
}

export const podeTransicionar = (de: StatusReserva, para: StatusReserva): boolean =>
  (TRANSICOES[de] ?? []).includes(para)

/** Ordem das colunas no Kanban — segue a jornada. */
export const ORDEM_STATUS: StatusReserva[] = [
  'RASCUNHO',
  'ENVIADA',
  'EM_ANALISE_ORIGINEI',
  'COM_PENDENCIA',
  'APROVADA_ORIGINEI',
  'REPROVADA_ORIGINEI',
  'EM_ESTRUTURACAO',
  'EM_NEGOCIACAO',
  'EM_ANALISE_PARCEIRO',
  'APROVADA_PARCEIRO',
  'REPROVADA_PARCEIRO',
  'EM_CONTRATACAO',
  'CONTRATO_ASSINADO',
  'EM_OPERACIONALIZACAO',
  'ATIVO',
  'ENCERRADA',
  'CANCELADA',
  'NAO_CONVERTIDA',
]

/** Terminais: só viram coluna quando têm cards, para não poluir o funil. */
export const STATUS_TERMINAIS: StatusReserva[] = ['ENCERRADA', 'CANCELADA', 'NAO_CONVERTIDA']
